/**
 * Utility functions for transforming activity data based on locale
 */
import {
  DEFAULT_TIMEZONE,
  formatRegions,
  normalizeSiteLanguage,
  pickDisplaySession,
  type ActivityLanguageValue,
  type ParticipationValue,
} from '@yoyoclub/shared';

import { localize } from '../i18n/script';
import type { PublicCourse } from '../services/courseApi';

interface Activity {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  slug: string;
  category: string;
  location?: string; // legacy fallback; dropped after Phase 6 migration
  locationDetails: string;
  region?: string[];
  ageMin: number;
  ageMax: number;
  price: number;
  adultPrice: number;
  childRequired?: boolean;
  // Effective values from the API (homepage redesign)
  participation?: ParticipationValue;
  language?: ActivityLanguageValue;
  maxParticipants: number;
  duration: number;
  status: string;
  host: {
    displayName: string;
    id: string;
  };
  images: Array<{
    imageUrl: string;
    order: number;
    isCover: boolean;
  }>;
  sessions: Array<{
    id?: string;
    startTime: string;
    endTime: string;
    maxCapacity: number;
    currentBookings?: number;
    price?: number | null; // Session child price in cents (null = use activity default)
    adultPrice?: number | null; // Session adult price in cents (null = use activity default)
  }>;
}

interface ActivityCardProps {
  id: string;
  /** Database id (favorites); passes have none. */
  activityId?: string;
  title: string;
  host: string;
  location: string;
  date: string;
  time: string;
  price: number;
  adultPrice: number;
  /** True when the displayed (min) price differs across sessions — card adds "From"/"起". */
  priceVaries?: boolean;
  childRequired?: boolean;
  ageMin: number;
  ageMax?: number;
  ageRange: string;
  imageUrl: string;
  itemType?: 'activity' | 'pass' | 'course';
  totalSessions?: number;
  startTime?: string;
  participation?: ParticipationValue;
  language?: ActivityLanguageValue;
}

export interface PassForCard {
  id: string;
  slug: string;
  titleCN: string | null;
  titleEN: string | null;
  price: number; // cents
  totalSessions: number;
  // Nested format (from /passes/active, pass detail APIs)
  activity?: {
    category?: string;
    location?: string;
    locationDetails?: string;
    region?: string[];
    ageMin?: number;
    ageMax?: number;
    duration?: number;
    childRequired?: boolean;
    host?: { displayName: string; id: string };
    images: Array<{ imageUrl: string; order: number; isCover: boolean }>;
  };
  // Flattened format (from /activities/featured API)
  category?: string;
  location?: string;
  locationDetails?: string;
  region?: string[];
  ageMin?: number;
  ageMax?: number;
  duration?: number;
  childRequired?: boolean;
  host?: { displayName: string; id: string };
  images?: Array<{ imageUrl: string; order: number; isCover: boolean }>;
  sessions: Array<{
    session?: {
      startTime: string;
      endTime: string;
    };
    startTime?: string;
    endTime?: string;
  }>;
}

/**
 * Get localized activity title based on current locale
 * @param activity - Activity object with titleEN and titleCN
 * @param locale - Current locale (e.g., 'en', 'zh-CN')
 * @returns Localized title
 */
export function getLocalizedTitle(
  activity: { titleCN?: string | null; titleEN?: string | null },
  locale: string
): string {
  return localize(activity.titleCN, activity.titleEN, locale);
}

/**
 * Transform backend API activity to ActivityCard props with locale support
 * @param activity - Activity object from backend API
 * @param locale - Current locale for date/time formatting and title selection
 * @returns Transformed activity card props or null if no sessions
 */
export function transformActivity(
  activity: Activity,
  locale: string,
  window?: { startMs?: number; endMs?: number }
): ActivityCardProps | null {
  const coverImage =
    activity.images.find((img) => img.isCover)?.imageUrl ||
    activity.images[0]?.imageUrl ||
    '/placeholder-activity.jpg'; // Fallback placeholder
  const displaySession = pickDisplaySession(
    activity.sessions,
    window ? { start: window.startMs, end: window.endMs } : undefined,
    Date.now()
  );

  if (!displaySession) {
    return null;
  }

  // Choose title based on current language
  const title = getLocalizedTitle(activity, locale);

  // Format date and time based on locale (always use activity timezone)
  const dateObj = new Date(displaySession.startTime);
  const formattedDate = dateObj.toLocaleDateString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: DEFAULT_TIMEZONE,
  });

  // Format time
  const startTime = dateObj.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: DEFAULT_TIMEZONE,
  });
  const endDate = new Date(displaySession.endTime);
  const endTime = endDate.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: DEFAULT_TIMEZONE,
  });

  // Card pricing rule:
  // - Family activities: use minimum effective child price across sessions
  // - Adult-only activities: use minimum effective adult price across sessions
  // - Adult free pricing should not pull family "from" price to 0
  // - priceVaries flags whether the chosen price actually differs across sessions.
  //   Cards show "From $X" only when true; otherwise display the fixed price.
  const isAdultOnly = activity.childRequired === false;
  const effectivePrices = activity.sessions.map((s) =>
    isAdultOnly ? (s.adultPrice ?? activity.adultPrice) : (s.price ?? activity.price)
  );
  const minPrice = Math.min(...effectivePrices);
  const maxPrice = Math.max(...effectivePrices);
  const priceVaries = effectivePrices.length > 1 && minPrice !== maxPrice;
  const minEffectiveAdultPrice = Math.min(
    ...activity.sessions.map((s) => s.adultPrice ?? activity.adultPrice)
  );

  return {
    id: activity.slug,
    activityId: activity.id,
    title,
    host: activity.host.displayName,
    // Card displays region only (smart truncation for ≥3 regions). locationDetails
    // is intentionally NOT shown on activity cards (incl. the Calendar view) —
    // it's reserved for ActivityDetail / PassDetail / Newsletter / emails per
    // docs/prd-multi-region.md Amendments (2026-05-14).
    location: formatRegions(activity.region, normalizeSiteLanguage(locale), 'smart'),
    date: formattedDate,
    time: `${startTime} - ${endTime}`,
    // Cards show the start only (prototype); `time` keeps the range for the calendar view.
    startTime,
    price: minPrice / 100,
    adultPrice: minEffectiveAdultPrice / 100,
    priceVaries,
    childRequired: activity.childRequired,
    ageMin: activity.ageMin,
    ageMax: activity.ageMax,
    ageRange:
      activity.ageMax >= 99 ? `${activity.ageMin}+` : `${activity.ageMin}-${activity.ageMax}`,
    imageUrl: coverImage,
    participation: activity.participation,
    language: activity.language,
  };
}

/**
 * Transform a Pass (with linked activity data) to ActivityCard props
 * Maps pass data to the same shape as transformActivity for unified card display
 */
/** A public recorded course (GET /courses or the homepage) as a card, addressed by slug. */
export function transformCourse(course: PublicCourse, locale: string): ActivityCardProps {
  return {
    id: course.slug,
    activityId: course.id,
    itemType: 'course',
    title: localize(course.titleCN, course.titleEN, locale),
    host: course.host?.displayName ?? '',
    location: '',
    date: '',
    time: '',
    price: course.price / 100,
    adultPrice: 0,
    ageMin: course.ageMin,
    ageMax: course.ageMax,
    ageRange: '',
    imageUrl: course.coverImage ?? '/placeholder-activity.jpg',
    participation: course.participation,
    language: course.language,
  };
}

export function transformPass(pass: PassForCard, locale: string): ActivityCardProps | null {
  // Support both nested (pass.activity.X) and flattened (pass.X) data formats
  const act = pass.activity;
  const images = act?.images || pass.images || [];
  const coverImage =
    images.find((img) => img.isCover)?.imageUrl ||
    images[0]?.imageUrl ||
    '/placeholder-activity.jpg';

  const title = getLocalizedTitle(pass, locale);

  // Use first pass session for date display
  const firstSession = pass.sessions?.[0];
  let formattedDate = '';
  let formattedTime = '';

  if (firstSession) {
    // Support both flattened ({ startTime }) and nested ({ session: { startTime } }) formats
    const startTimeStr = firstSession.session?.startTime || firstSession.startTime;
    const endTimeStr = firstSession.session?.endTime || firstSession.endTime;

    if (startTimeStr && endTimeStr) {
      const dateObj = new Date(startTimeStr);
      formattedDate = dateObj.toLocaleDateString(locale, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: DEFAULT_TIMEZONE,
      });

      const startTime = dateObj.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: DEFAULT_TIMEZONE,
      });
      const endDate = new Date(endTimeStr);
      const endTime = endDate.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: DEFAULT_TIMEZONE,
      });
      formattedTime = `${startTime} - ${endTime}`;
    }
  }

  const ageMin = act?.ageMin ?? pass.ageMin ?? 0;
  const ageMax = act?.ageMax ?? pass.ageMax ?? 99;
  const hostName = act?.host?.displayName || pass.host?.displayName || '';
  // Pass cards mirror activity cards: region only, smart-truncated.
  const loc = formatRegions(act?.region ?? pass.region, normalizeSiteLanguage(locale), 'smart');

  return {
    id: pass.slug,
    title,
    host: hostName,
    location: loc,
    date: formattedDate,
    time: formattedTime,
    price: pass.price / 100,
    adultPrice: 0,
    childRequired: act?.childRequired ?? pass.childRequired,
    ageMin,
    ageRange: ageMax >= 99 ? `${ageMin}+` : `${ageMin}-${ageMax}`,
    imageUrl: coverImage,
    itemType: 'pass',
    totalSessions: pass.totalSessions,
  };
}
